import React, { useState, useEffect, useReducer } from "react";
import LayoutContent from "../../LayoutContent";
import SettingsNavSubMenu from "../SettingsNavSubMenu";
import Header from "../../Header/Header";
import Breadcrumb from "../../Header/Breadcrumb";
import MtiButton from "../../Buttons/MtiButton";
import TabPanel from "../../PlateTypeEditor/TabPanel";
import { useConfirm } from "../../Hooks/WindowHooks";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { getImagerCapabilities } from "../../../redux/reducers/ImagerCapabilitiesReducer";
import { getModules } from "../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../Model/EModuleType";
import { ECameraCalibrationType } from "./CameraCalibration/CameraCalibrationContext";
import ImagerUtil from "../../../Utility/ImagerUtil";
import {
  ImagerCalibration,
  PixelSizeCalibration,
} from "../../../Model/ImagerCalibration";
import CalibrationApi from "../../../api/CalibrationApi";
import { CameraCalibrationContext } from "./CameraCalibration/CameraCalibrationContext";
import CameraCalibrationContent from "./CameraCalibration/CameraCalibrationContent";
import CameraCalibrationCommandPanel from "./CameraCalibration/CameraCalibrationCommandPanel";
import HeaderButton from "../../Header/HeaderButton";
import SwitchButton from "../../Buttons/SwitchButton";
import { EImagingModuleStatus } from "../../../Model/ModuleState";
import ImagerApi from "../../../api/ImagerApi";
import LiveImage from "../../Imager/LiveImage";
import MotorCalibrationCommandPanel from "./MotorCalibration/MotorCalibrationCommandPanel";
import LiveStreamHeaderButton from "../../Common/LiveStreamHeaderButton";
import { ELightPath } from "../../../Model/CaptureProfile";

type ImagerCalibrationType = "CAMERA CALIBRATION" | "MOTOR CALIBRATION";

const ImagerCalibrationTabs: ImagerCalibrationType[] = [
  "CAMERA CALIBRATION",
  "MOTOR CALIBRATION",
];

export default function SettingsImager() {
  const [onApply, setOnApply] = useState({
    execute: () => {},
    disabled: true,
  });
  const [onCancel, setOnCancel] = useState({
    execute: () => {},
    disabled: true,
  });
  const imagerModules = useTypedSelector((state) =>
    getModules(state, EModuleType.IMAGER)
  );
  const imagerIdentifier = imagerModules[0].identifier;
  const imagerCapabilitiesCollection = useTypedSelector((state) =>
    getImagerCapabilities(state)
  );
  const [imagerCalibration, setImagerCalibration] = useState(
    undefined as ImagerCalibration | undefined
  );
  const [cameraCalibrationType, setCameraCalibrationType] = useState(
    ECameraCalibrationType.CameraCalibrationSelection
  );
  const [lightPath, setLightPath] = useState(
    imagerCapabilitiesCollection.byId[imagerIdentifier].lightpathCollection
      .allIds[0]
  );
  const lightpathWithOpticMotor = ImagerUtil.getLightPathWithOpticMotor(
    imagerCapabilitiesCollection.byId[imagerIdentifier]
  );

  const [isCalibrationUpdated, setIsCalibrationUpdated] = useState(false);
  const [manualControlEnabled, setManualControlEnabled] = useState(imagerIdentifier != null ? true : false)

  //pixelSize states
  const [gridSize, setGridSize] = useState(100);
  const [numberOfSquares, setNumberOfSquares] = useState(1);
  const [lineLength, setLineLength] = useState(-1);
  const [pixelSizeCalibration, setPixelSizeCalibration] = useState(
    undefined as PixelSizeCalibration | undefined
  );
  const [pixelSizeValue, setPixelSizeValue] = useState(0);

   const [showDrawLine, setShowDrawLine] = useState(true)

  //header states
  const freerunCapture = useTypedSelector((state) =>
    imagerIdentifier != null
      ? state.moduleStateStore.moduleStateById[imagerIdentifier].imager
          ?.freerunCapture ?? false
      : false
  );

  const toggleLiveImg = (isOn: boolean) => {
    if (imagerIdentifier != null) {
      isOn
        ? ImagerApi.startStreaming(imagerIdentifier)
        : ImagerApi.stopStreaming(imagerIdentifier);
    }
  };

  //Tab Panel states
  const userConfirmLeaveTab = useConfirm(
    "Changes are not saved, are you sure you want to leave Settings - Imager - X"
  );
  userConfirmLeaveTab.setOnCancel(() => {
    return;
  });
  const [imagerCalibrationType, setImagerCalibrationType] = useState(
    "CAMERA CALIBRATION" as ImagerCalibrationType
  );
  const selectedTabIndex = ImagerCalibrationTabs.findIndex(
    (h) => h === imagerCalibrationType
  );

  const onClickTab = (header: string) => {
    if (
      isCalibrationUpdated &&
      imagerCalibrationType !== (header as ImagerCalibrationType)
    ) {
      userConfirmLeaveTab.setOnOk(() => {
        setImagerCalibrationType(header as ImagerCalibrationType);
        setIsCalibrationUpdated(false);
      });
      userConfirmLeaveTab.showConfirmationAlert();
      return;
    } else {
      setImagerCalibrationType(header as ImagerCalibrationType);
    }
  };

  const handleApply = () => {
    onApply.execute()
    setShowDrawLine(false)
  }

  const handleCancel = () => {
    onCancel.execute()
    setShowDrawLine(false)
  }

  const handleUpdateCalibration = (status: boolean) => {
    setIsCalibrationUpdated(status)
    setShowDrawLine(true)
  }

  useEffect(() => {
    userConfirmLeaveTab.setOnOk(onCancel.execute);
    userConfirmLeaveTab.setShowOnLeave(isCalibrationUpdated);
    switch (imagerCalibrationType) {
      case "CAMERA CALIBRATION":
        userConfirmLeaveTab.setMessage(
          "Changes are not saved, are you sure you want to leave Settings - Imager - Camera Calibrations"
        );
        break;
      case "MOTOR CALIBRATION":
        userConfirmLeaveTab.setMessage(
          "Changes are not saved, are you sure you want to leave Settings - Imager - Motor Calibrations"
        );
        break;
    }
  }, [imagerCalibrationType, isCalibrationUpdated, onCancel.execute, userConfirmLeaveTab]);

  useEffect(() => {
    CalibrationApi.getImagerCalibration(imagerIdentifier).then(
      (imagerCalibration) => {
        setImagerCalibration(imagerCalibration);
      }
    );
  }, [imagerIdentifier]);

  useEffect(() => {
    setPixelSizeCalibration(
      ImagerUtil.getPixelSizeCalibration(imagerCalibration, lightPath)
    );
  }, [imagerCalibration, lightPath]);

  useEffect(() => {
    if (pixelSizeCalibration !== undefined) {
      setPixelSizeValue(
        pixelSizeCalibration?.lengthInMicron /
          pixelSizeCalibration?.lengthInPixel
      );
    }
  }, [pixelSizeCalibration]);

  useEffect(() => {
    const preventReload = (e: BeforeUnloadEvent) => {
      if (isCalibrationUpdated) {
        // Cancel the event
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = "";
      } else {
        delete e["returnValue"];
      }
    };

    window.addEventListener("beforeunload", preventReload);

    return () => {
      window.removeEventListener("beforeunload", preventReload);
    };
  }, [isCalibrationUpdated]);

  const cameraCalibrationContextState = {
    lightpath: lightPath,
    setLightpath: setLightPath,
    calibration: {
      cameraCalibrationType: cameraCalibrationType,
      setCameraCalibrationType: setCameraCalibrationType,
      pixelSizeCalibration: pixelSizeCalibration,
      setPixelSizeCalibration: setPixelSizeCalibration,
      isCalibrationUpdated: isCalibrationUpdated,
      setIsCalibrationUpdated: setIsCalibrationUpdated,
    },
    pixelSize: {
      gridSize: gridSize,
      setGridSize: setGridSize,
      numberOfSquares: numberOfSquares,
      setNumberOfSquares: setNumberOfSquares,
      lineLength: lineLength,
      setLineLength: setLineLength,
      pixelSizeValue: pixelSizeValue,
      setPixelSizeValue: setPixelSizeValue,
    },
  };

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              { title: "Imager", to: "/settings/imager", disabled: false },
            ]}
          />
          <Header.RightSide>
            {imagerIdentifier != null && (
              <LiveStreamHeaderButton
                freerunCapture={freerunCapture}
                imagerIdentifier={imagerIdentifier}
                disabled={!manualControlEnabled}
              />
            )}
            <MtiButton
              size="large"
              variant="secondary"
              style={{
                margin: "auto 10px auto 25px",
              }}
              disabled={onCancel.disabled}
              onClick={handleCancel}
            >
              CANCEL
            </MtiButton>
            <MtiButton
              size="large"
              disabled={onApply.disabled}
              style={{
                margin: "auto 16px auto 10px",
              }}
              onClick={handleApply}
            >
              APPLY
            </MtiButton>
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.SubMenu>
        <SettingsNavSubMenu />
      </LayoutContent.SubMenu>
      <LayoutContent.Content>
        <TabPanel
          id="imager_tab"
          collapsible={false}
          variant="alternate"
          headerHeight={48}
          headerItemWidth={221}
          style={{ height: "100%", marginLeft: 12 }}
          onClickTab={onClickTab}
          allowChange={false}
          defaultSelectedIndex={selectedTabIndex}
          activeTabStyle={{
            padding: "0px",
          }}
          headerStyle={{
            margin: "0px 0px",
          }}
        >
          {lightpathWithOpticMotor !== ELightPath.Disabled
            ? {
                "CAMERA CALIBRATION": (
                  <CameraCalibrationContext.Provider
                    value={cameraCalibrationContextState}
                  >
                    <CameraCalibrationContent
                      imagerIdentifier={imagerIdentifier}
                      setIsCalibrationUpdated={handleUpdateCalibration}
                      showDrawLine={showDrawLine}
                    />
                  </CameraCalibrationContext.Provider>
                ),
                "MOTOR CALIBRATION": (
                  <LiveImage
                    imagerIdentifier={imagerIdentifier}
                    style={{
                      borderTop: "1px solid #B4B4B4",
                      borderLeft: "1px solid #B4B4B4",
                    }}
                  />
                ),
              }
            : {
                "CAMERA CALIBRATION": (
                  <CameraCalibrationContext.Provider
                    value={cameraCalibrationContextState}
                  >
                    <CameraCalibrationContent
                      imagerIdentifier={imagerIdentifier}
                      setIsCalibrationUpdated={handleUpdateCalibration}
                      showDrawLine={showDrawLine}
                    />
                  </CameraCalibrationContext.Provider>
                ),
              }}
        </TabPanel>
      </LayoutContent.Content>
      <LayoutContent.CommandPanel
        style={{
          paddingTop: 53,
          borderTop: "6px solid rgb(130, 130, 130)",
          WebkitBoxShadow: "none",
          MozBoxShadow: "none",
          boxShadow: "none",
          paddingRight: 16,
        }}
      >
        {imagerCalibrationType === "CAMERA CALIBRATION" && (
          <CameraCalibrationContext.Provider
            value={cameraCalibrationContextState}
          >
            <CameraCalibrationCommandPanel
              freerunCapture={freerunCapture}
              imagerIdentifier={imagerIdentifier}
              imagerCalibration={imagerCalibration}
              setImagerCalibration={setImagerCalibration}
              setOnApply={setOnApply}
              setOnCancel={setOnCancel}
              isCalibrationUpdated={isCalibrationUpdated}
              setIsCalibrationUpdated={setIsCalibrationUpdated}
              setManualControlEnabled={setManualControlEnabled}
            />
          </CameraCalibrationContext.Provider>
        )}
        {imagerCalibrationType === "MOTOR CALIBRATION" &&
          lightpathWithOpticMotor !== ELightPath.Disabled && (
            <MotorCalibrationCommandPanel
              imagerIdentifier={imagerIdentifier}
              imagerCalibration={imagerCalibration}
              setImagerCalibration={setImagerCalibration}
              setOnApply={setOnApply}
              setOnCancel={setOnCancel}
              isCalibrationUpdated={isCalibrationUpdated}
              setIsCalibrationUpdated={setIsCalibrationUpdated}
            />
          )}
      </LayoutContent.CommandPanel>
    </LayoutContent>
  );
}
